import React, { useState } from 'react';
import PropTypes from 'prop-types';

import Box, { BoxProps as MuiBoxProps } from '@mui/material/Box';
import { Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';

import { globalColours } from '@classity-edtech/ui-base/dist/style/source';
import {
  FullscreenExitIcon,
  FullscreenIcon,
  PlayIcon,
} from '@classity-edtech/ui-base/dist/components/Displays';

export interface VideoComponentProps extends MuiBoxProps {
  title: string;
  vidSrc: string;
}

export const VideoComponent = ({ className, title, vidSrc }: VideoComponentProps) => {
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [videoRef, setVideoRef] = useState<HTMLVideoElement | null>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);

  return (
    <Box sx={{ position: 'relative' }}>
      {!isVideoPlaying && (
        <Box
          sx={{
            borderRadius: '8px 8px 0 0',
            background: globalColours['$global-colour-palette-gray-50'],
            p: 1.5,
            position: 'absolute',
            width: '100%',
            zIndex: 10,
            top: 0,
          }}
        >
          <Typography variant='body3'>{title}</Typography>
        </Box>
      )}
      <Box
        sx={{
          aspectRatio: '4/3',
          width: '100%',
          position: 'relative',
          borderRadius: '8px 8px 0 0',
          overflow: 'hidden',
        }}
      >
        <video
          ref={setVideoRef}
          src={vidSrc}
          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          onTimeUpdate={e => setCurrentTime(e.currentTarget.currentTime)}
          onLoadedMetadata={e => setDuration(e.currentTarget.duration)}
          onPlay={() => setIsVideoPlaying(true)}
          onPause={() => setIsVideoPlaying(false)}
        />
        {!isVideoPlaying && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'rgba(0,0,0,0.3)',
              cursor: 'pointer',
            }}
            onClick={() => videoRef?.play()}
          >
            <PlayIcon sx={{ width: '82px', height: '82px' }} />
          </Box>
        )}
      </Box>
      <Box
        sx={{
          borderRadius: '8px 8px 0 0',
          background: globalColours['$global-colour-background-overlay'],
          p: 1.5,
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
        }}
      >
        <PlayArrowIcon
          sx={{
            width: '28px',
            height: '28px',
            color: globalColours['$global-colour-base-dark'],
            cursor: 'pointer',
          }}
          onClick={() => (isVideoPlaying ? videoRef?.pause() : videoRef?.play())}
        />

        <Typography variant='body3'>
          {Math.floor(currentTime / 60)
            .toString()
            .padStart(2, '0')}
          :
          {Math.floor(currentTime % 60)
            .toString()
            .padStart(2, '0')}
        </Typography>
        <Box
          sx={{ position: 'relative', width: '100%', cursor: 'pointer' }}
          onClick={e => {
            if (videoRef && duration) {
              const rect = e.currentTarget.getBoundingClientRect();
              const clickX = e.clientX - rect.left;
              const newTime = (clickX / rect.width) * duration;
              videoRef.currentTime = newTime;
            }
          }}
        >
          <Box
            sx={{
              width: '15px',
              height: '15px',
              borderRadius: '100%',
              background: globalColours['$global-colour-base-dark'],
              position: 'absolute',
              top: '-7px',
              left: `${duration ? (currentTime / duration) * 100 : 0}%`,
              transform: 'translateX(-50%)',
            }}
          ></Box>
          <Box
            sx={{
              height: '2px',
              background: globalColours['$global-colour-base-dark'],
              width: `${duration ? (currentTime / duration) * 100 : 0}%`,
            }}
          ></Box>
          <Box
            sx={{
              height: '2px',
              background: globalColours['$global-colour-border-neutral'],
              width: '100%',
              position: 'absolute',
              top: 0,
              zIndex: -1,
            }}
          ></Box>
        </Box>
        <Typography variant='body3'>
          {Math.floor(duration / 60)
            .toString()
            .padStart(2, '0')}
          :
          {Math.floor(duration % 60)
            .toString()
            .padStart(2, '0')}
        </Typography>
        {isFullscreen ? (
          <FullscreenExitIcon
            sx={{
              width: '16px',
              height: '16px',
              color: globalColours['$global-colour-base-dark'],
              cursor: 'pointer',
            }}
            onClick={() => {
              document.exitFullscreen();
              setIsFullscreen(false);
            }}
          />
        ) : (
          <FullscreenIcon
            sx={{
              width: '16px',
              height: '16px',
              color: globalColours['$global-colour-base-dark'],
              cursor: 'pointer',
            }}
            onClick={() => {
              videoRef?.requestFullscreen();
              setIsFullscreen(true);
            }}
          />
        )}
      </Box>
    </Box>
  );
};

VideoComponent.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string,
};

VideoComponent.defaultProps = {
  children: null,
  className: null,
};

export default styled<any>(VideoComponent)(
  ({ theme }) => `
`,
);
